/**
 * Per-variant regions editor for a single provider row.
 *
 * Wraps :component:`RegionsCell` once per variant the kind exposes and
 * adds a per-variant enabled checkbox so operators can toggle, say,
 * RunPod community on while leaving secure on at the same time, or
 * AWS spot on at a region-pinned subset while the ondemand variant
 * stays enabled at "all regions".
 *
 * Single-variant kinds (lambda, paperspace, ...) collapse to the same
 * presentation a bare `RegionsCell` would render so the table doesn't
 * grow visually for clouds without a placement axis.
 *
 * Persistence model
 * -----------------
 *
 * Both halves of the variant axis live on ``ProviderEntry.values`` as
 * flat string keys (the existing schema constraint that values is a
 * ``dict[str, str]``):
 *
 *   * ``variant_enabled__<key>`` - "true" / "false"
 *   * ``enabled_regions__<key>`` - CSV (REGION or REGION:ZONE)
 *
 * The wrapping page (Global / Service / Pool Providers) owns the write
 * path; this component just hands it the (variantKey, regions, enabled)
 * tuple on every change and lets the page batch the patch through its
 * existing ``regionsToValuePatch`` + master-enabled wiring.
 */
import {
  PROVIDER_META,
  defaultVariantForKind,
  getEntryRegions,
  variantsForKind,
  type ProviderEntry,
  type ProviderKind,
} from "@/api/local/providers";
import { RegionsCell } from "@/components/RegionsCell";

type RegionSuggestion = { value: string; label?: string };

interface Props {
  kind: ProviderKind;
  entry: ProviderEntry | undefined;
  suggestions?: ReadonlyArray<RegionSuggestion>;
  readOnly?: boolean;
  /**
   * Called when the operator either edits a variant's region list or
   * toggles the variant's enabled flag. The page rebuilds the values
   * patch via ``regionsToValuePatch(regions, kind, variantKey)`` plus
   * a ``{ variant_enabled__<v>: enabled ? "true" : "false" }`` entry
   * and forwards everything through its own update hook.
   */
  onChangeVariant: (variantKey: string, next: { regions: string[]; enabled: boolean }) => void;
}

function isVariantEnabled(
  entry: ProviderEntry | undefined,
  kind: ProviderKind,
  variantKey: string,
): boolean {
  const raw = entry?.values?.[`variant_enabled__${variantKey}`];
  if (raw !== undefined) {
    return raw === "true";
  }
  // Pre-migration / freshly-created row with no variant_enabled keys
  // yet: treat the default variant as on if the row's master enabled
  // flag is true (mirrors the controller-side fallback in
  // ``placements.py::_resolve_for_kind``).
  const def = defaultVariantForKind(kind);
  if (def && def.key === variantKey) {
    return entry?.enabled ?? false;
  }
  return false;
}

export function VariantsRegionsCell({
  kind,
  entry,
  suggestions,
  readOnly = false,
  onChangeVariant,
}: Props) {
  const variants = variantsForKind(kind);
  const supportsRegions = PROVIDER_META[kind]?.supportsRegions ?? false;

  // Single-variant kinds: render one cell exactly like the bare
  // RegionsCell did before variants existed. Avoids a visual change
  // for clouds without a placement axis.
  if (variants.length <= 1) {
    const v = variants[0];
    const regions = getEntryRegions(entry, kind, v?.key);
    return (
      <RegionsCell
        regions={regions}
        onChange={(next) =>
          onChangeVariant(v?.key ?? "default", {
            regions: next,
            enabled: entry?.enabled ?? true,
          })
        }
        suggestions={supportsRegions ? suggestions : undefined}
        readOnly={readOnly || !supportsRegions}
        emptyLabel={supportsRegions ? "all regions" : "n/a"}
      />
    );
  }

  return (
    <div className="variants-regions" style={{ display: "grid", gap: 6 }}>
      {variants.map((v) => {
        const variantEnabled = isVariantEnabled(entry, kind, v.key);
        const regions = getEntryRegions(entry, kind, v.key);
        return (
          <div
            key={v.key}
            className="variants-regions__row"
            style={{
              display: "grid",
              gridTemplateColumns: "auto auto 1fr",
              gap: 8,
              alignItems: "center",
            }}
          >
            <label
              style={{
                display: "inline-flex",
                gap: 4,
                alignItems: "center",
                fontSize: 12,
                whiteSpace: "nowrap",
              }}
              title={`${v.label} (${v.key})`}
            >
              <input
                type="checkbox"
                checked={variantEnabled}
                disabled={readOnly}
                onChange={(e) =>
                  onChangeVariant(v.key, {
                    regions,
                    enabled: e.target.checked,
                  })
                }
                aria-label={`enable ${v.label}`}
              />
              <span>{v.label}</span>
            </label>
            <span className="muted mono" style={{ fontSize: 11 }}>
              {v.useSpot ? "spot" : "ondemand"}
              {v.runpodCloudType ? ` · ${v.runpodCloudType.toLowerCase()}` : ""}
            </span>
            <RegionsCell
              regions={regions}
              onChange={(next) =>
                onChangeVariant(v.key, {
                  regions: next,
                  enabled: variantEnabled,
                })
              }
              suggestions={suggestions}
              readOnly={readOnly}
              emptyLabel="all regions"
            />
          </div>
        );
      })}
    </div>
  );
}
